import { BlockBaseCommand } from './BlockBaseCommand';
import type { BlockCommandDependencies } from './BlockBaseCommand';
import { AcknowledgmentResponseStatus } from '../../../services/socket/SocketEvents';
import { setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { updateGridBlockState, updateGridDimensionConfig, updateGridPositionConfig } from '../grid/reduxStore/editorSlice';
import { saveContentWithSocket, updateBlockDimensionsViaSocket, updateBlockPositionViaSocket } from '../grid/reduxStore/saveHandlers';
import type { DimensionPositionStateChangedType } from '../hooks/UseBlockDimensionChangedHandler';

export interface UpdateBlockContentCommandParams {
  blockId: string;
  sectionId: string;
  previousContent: string;
  newContent: string;
  previousDimension?: DimensionPositionStateChangedType;
  newDimension?: DimensionPositionStateChangedType;
}

export class UpdateBlockContentCommand extends BlockBaseCommand {
  private readonly blockId: string;
  private readonly sectionId: string;
  private readonly previousContent: string;
  private readonly newContent: string;
  private readonly previousDimension?: DimensionPositionStateChangedType;
  private readonly newDimension?: DimensionPositionStateChangedType;

  constructor(dependencies: BlockCommandDependencies, params: UpdateBlockContentCommandParams) {
    super(dependencies);
    this.blockId = params.blockId;
    this.sectionId = params.sectionId;
    this.previousContent = params.previousContent;
    this.newContent = params.newContent;
    this.previousDimension = params.previousDimension;
    this.newDimension = params.newDimension;
  }

  getCurrentBlockId(): string {
    return this.blockId;
  }

  applyOptimistic(): void {
    this.applyContentToRedux(this.newContent);
    if (this.newDimension) {
      this.applyDimensionToRedux(this.newDimension);
    }
  }

  async undo(): Promise<boolean> {
    return this.applyAndPersist(this.previousContent, this.previousDimension);
  }

  async redo(): Promise<boolean> {
    return this.applyAndPersist(this.newContent, this.newDimension);
  }

  async persistToSocket(): Promise<boolean> {
    try {
      this.setSaving();

      const response = await this.publishContentToSocket(this.newContent);

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.rollback('previous');
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.rollback('previous');
        return false;
      }

      const finalResponse = response;

      if (this.newDimension) {
        const dimensionSuccess = await this.persistDimensionToSocket(this.newDimension);
        if (!dimensionSuccess) {
          this.rollback('previous');
          return false;
        }
      }

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: finalResponse.status,
          errorCode: finalResponse.errorCode,
        })
      );

      return true;
    } catch (error) {
      console.error('UpdateBlockContentCommand: persistToSocket failed', error);
      this.rollback('previous');
      return false;
    }
  }

  private async applyAndPersist(content: string, dimension?: DimensionPositionStateChangedType): Promise<boolean> {
    try {
      this.setSaving();

      this.applyContentToRedux(content);
      if (dimension) {
        this.applyDimensionToRedux(dimension);
      }

      const response = await this.publishContentToSocket(content);

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.rollback(content === this.newContent ? 'previous' : 'new');
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.rollback(content === this.newContent ? 'previous' : 'new');
        return false;
      }

      if (dimension) {
        const dimensionSuccess = await this.persistDimensionToSocket(dimension);
        if (!dimensionSuccess) {
          this.rollback(content === this.newContent ? 'previous' : 'new');
          return false;
        }
      }

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          errorCode: response.errorCode,
        })
      );

      return true;
    } catch (error) {
      console.error('UpdateBlockContentCommand: applyAndPersist failed', error);
      this.rollback(content === this.newContent ? 'previous' : 'new');
      return false;
    }
  }

  private rollback(target: 'previous' | 'new'): void {
    const content = target === 'previous' ? this.previousContent : this.newContent;
    const dimension = target === 'previous' ? this.previousDimension : this.newDimension;

    this.applyContentToRedux(content);
    if (dimension) this.applyDimensionToRedux(dimension);
    this.setNotSaved();
  }

  private applyContentToRedux(content: string): void {
    this.dispatch(
      updateGridBlockState({
        blockId: this.blockId,
        sectionId: this.sectionId,
        content,
      })
    );
  }

  private hasPositionChanged(): boolean {
    return (
      !this.previousDimension ||
      !this.newDimension ||
      this.previousDimension.xAxisPx !== this.newDimension.xAxisPx ||
      this.previousDimension.yAxisPx !== this.newDimension.yAxisPx
    );
  }

  private applyDimensionToRedux(dimensions: DimensionPositionStateChangedType): void {
    if (this.hasPositionChanged()) {
      this.dispatch(
        updateGridPositionConfig({
          sectionId: this.sectionId,
          blockId: this.blockId,
          x: dimensions.xAxisPx,
          y: dimensions.yAxisPx,
        })
      );
    }

    this.dispatch(
      updateGridDimensionConfig({
        sectionId: this.sectionId,
        blockId: this.blockId,
        width: dimensions.widthPx,
        height: dimensions.heightPx,
      })
    );
  }

  private publishContentToSocket(content: string): Promise<any> {
    return new Promise((resolve) => {
      this.dispatch(
        saveContentWithSocket({
          blockId: this.blockId,
          content,
          userId: this.userId,
          socketClient: this.socketClient,
          callback: (response) => {
            resolve(response);
          },
        })
      );
    });
  }

  private async persistDimensionToSocket(dimensions: DimensionPositionStateChangedType): Promise<boolean> {
    const dimensionResponse = await updateBlockDimensionsViaSocket(this.socketClient, {
      blockId: this.blockId,
      userId: this.userId,
      dimensions: {
        width_px: dimensions.widthPx,
        height_px: dimensions.heightPx,
      },
    });

    if (this.checkDocumentLockStatus(dimensionResponse.errorCode)) {
      return false;
    }

    if (dimensionResponse.status !== AcknowledgmentResponseStatus.OK) {
      console.error('Dimension update failed:', dimensionResponse);
      return false;
    }

    if (this.hasPositionChanged()) {
      const positionResponse = await updateBlockPositionViaSocket(this.socketClient, {
        blockId: this.blockId,
        userId: this.userId,
        position: {
          left_px: dimensions.xAxisPx,
          top_px: dimensions.yAxisPx,
        },
      });

      if (this.checkDocumentLockStatus(positionResponse.errorCode)) {
        return false;
      }

      if (positionResponse.status !== AcknowledgmentResponseStatus.OK) {
        console.error('Position update failed:', positionResponse);
        return false;
      }
    }

    return true;
  }

  toJSON(): Record<string, any> {
    return {
      blockId: this.blockId,
      sectionId: this.sectionId,
      previousContentLength: this.previousContent.length,
      newContentLength: this.newContent.length,
      hasDimensions: !!this.newDimension,
    };
  }
}
